import {Template} from "../../../components/template/template.jsx";
import {Card} from "../../../components/card/card.jsx";
import React, {useCallback, useMemo, useRef, useState} from "react";
import {useFormNavigation} from "../../../hooks/use-form-navigation.js";
import {DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL} from "../../../utils/ag-grid-columns-utils.js";
import {useQuery} from "@tanstack/react-query";
import InputForm from "../../../components/input/input-form.jsx";
import JourneyService from "../../../services/web/journey.service.js";
import {
    agGridShiftComparator,
    workScheduleSpecialCases, workScheduleSpecialCasesMap
} from "../work-schedule/work-schedule/form/utils/work-schedule-utils.js";
import SelectComponent from "../../../components/select/select-component.jsx";
import Button from "../../../components/button/button.jsx";
import {FaEraser, FaPlus, FaSearch} from "react-icons/fa";
import {AgGridReact} from "ag-grid-react";
import aggridUtils from "../../../utils/aggrid-utils.js";
import TeamService from "../../../services/web/team-service.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";

const initialFilter = {
    name: "",
    status: "active",
    scale: null
}

const statusOptions = [
    {value: "active", label: "Ativo"},
    {value: "inactive", label: "Inativo"}
]

const pathModel = {
    create: {
        dev: `/team/form`,
        isBlank: false
    },
    edit: {
        dev: `/team/form`,
        isBlank: true
    }
}

const specialCases = workScheduleSpecialCasesMap;
const specialCasesArr = workScheduleSpecialCases;
const TeamList = () => {
    const [filter, setFilter] = useState(initialFilter);
    const [statusFilter, setStatusFilter] = useState(initialFilter.status);
    const columnDefs = useMemo(() => [
        { field: 'id', headerName: "ID", hide: true},
        { field: 'name', headerName: "Nome", flex: 1, filter: 'agTextColumnFilter',
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }},
        { field: 'scale', headerName: "Jornada de trabalho", flex: 1, filter: 'agTextColumnFilter',
            valueFormatter: params => specialCases[params.value] ?? params.value,
            comparator: (a, b, nodeA, nodeB, isInverted) => {
                return aggridUtils.nullLastComparator(a, b, nodeA, nodeB, isInverted);
            }
        },
        { field: 'start_hour', headerName: "Horário inicial", flex: 1,
            valueFormatter: params => params.value?.slice(0, 5)},
    ], []);
    const [gridApi, setGridApi] = useState(null);
    const gridRef = useRef(null);
    const { onRowClicked, openForm } = useFormNavigation(pathModel);

    const defaultColDef = DEFAULT_COL_DEF_NO_RESIZE_SUPPRESS_ALL

    const { data = [] } = useQuery({
        queryFn: TeamService.list,
        queryKey: ["team"]
    })

    const filteredData = useMemo(() => {
        if (statusFilter === "") return data;

        return data.filter(team => team.status === (statusFilter === "active"));
    }, [data, statusFilter]);

    const handleInput = (target) => {
        setFilter(prevState => ({...prevState, [target.id]: target.value}));
    }

    const {data: journeys, isSuccess: isSuccessJourneys} = useQuery({
        queryFn: () => JourneyService.getAllJourneys()
            .then(({data}) => data.sort((a,b) => agGridShiftComparator(a.pattern,b.pattern)).map(el => ({value: el.pattern,
                label: specialCasesArr.some(scale => scale.id === el.pattern) ?
                    specialCasesArr.find(scale => scale.id === el.pattern).name :
                    el.pattern})) ?? []),
        queryKey: ['work-schedule-journeys'],
    })

    const search = useCallback(() => {
        if (!gridApi) return;

        const filterModel = {};

        Object.entries(filter).forEach(([field, value]) => {
            if (field === "status" || value === null || value === "") return;

            filterModel[field] = {
                filterType: "text",
                type: field === "scale" ? "equals" : "contains",
                filter: value
            };
        });

        setStatusFilter(filter.status);

        gridApi.setFilterModel(filterModel);
        gridApi.onFilterChanged();
    }, [filter, gridApi]);

    useGlobalKey("Enter", search);

    const clearFilter = useCallback(() => {
        setFilter(initialFilter);
        setStatusFilter(initialFilter.status);
        const api = gridRef.current?.api;
        if (api) {
            api.setFilterModel(null);
            api.onFilterChanged();
        }
    }, []);

    return (
        <Template>
            <Template.Header>
                <Template.Title>
                    Equipes
                </Template.Title>
                <Button onClick={openForm}><FaPlus/>Adicionar equipe</Button>
            </Template.Header>
            <Template.Body>
                <Card>
                    <Card.Body className="flex gap-4 items-end w-full">
                        <div className="flex-1">
                            <InputForm
                                id="name"
                                label="Nome"
                                value={filter.name}
                                onChange={({target}) => handleInput(target)}/>
                        </div>

                        <div className="flex-1">
                            <SelectComponent
                              value={journeys?.find(option => filter.scale === option?.value) ?? null}
                              options={journeys}
                              label={"Jornada de trabalho"}
                              onChange={option => {
                                  setFilter(prevState => ({
                                      ...prevState,
                                      scale: option?.value ?? null
                                  }))
                              }}
                            />
                        </div>

                        <div className="flex-1">
                            <SelectComponent
                                id="status"
                                value={statusOptions.find(option => filter.status === option.value) ?? null}
                                options={statusOptions}
                                label="Status"
                                onChange={option => {
                                    setFilter(prevState => ({
                                        ...prevState,
                                        status: option?.value ?? ""
                                    }))
                                }}
                            />
                        </div>

                        <div className="flex gap-2">
                            <Button onClick={clearFilter} title="Limpar" target="primary-outline"><FaEraser/></Button>
                            <Button onClick={search} title="Buscar"><FaSearch/></Button>
                        </div>
                    </Card.Body>
                </Card>
                <Card className="mt-2">
                    <Card.Body>
                        <div className="h-[calc(100vh-153px)] aggrid-row-pointer">
                            <AgGridReact
                                onGridReady={(params) => {
                                    setGridApi(params.api);
                                }}
                                ref={gridRef}
                                rowData={filteredData}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                pagination
                                onRowClicked={onRowClicked}
                                paginationPageSize={100}
                                paginationPageSizeSelector={[20, 50, 100, 500]}
                                animateRows={false}
                                suppressCellFocus={true}
                                gridOptions={{localeText: aggridUtils.localization()}}
                                theme={aggridUtils.generalTheme()}
                                onFilterChanged={aggridUtils.filterChangedNoRowsOverlay}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </Template.Body>
        </Template>
    )
}

export default TeamList;